// Pure helpers for the IT apps offboarding checklist on a leaver's Connected
// apps tab (spec §4.3): progress across Google apps and IT items, the leaver
// banner, and item naming.
import { formatDate } from '../../../lib/formatDate';

const allItems = (itData) => [...((itData && itData.close_manually) || []), ...((itData && itData.remove_seat) || [])];

// 'off' covers "flag off", "not a leaver" and "not available to this viewer".
export const itEffectiveStatus = (itStatus, itData) => (
  itStatus === 'ready' && !(itData && itData.applicable) ? 'off' : (itStatus || 'off')
);

/**
 * The "{done} of {total} closed" counter and the completion line.
 * - IT off: Google app-kind rows only, exactly as before this feature.
 * - IT loading or failed: no counter and never complete; a Google-only total
 *   would be a partial number presented as the whole job.
 * - IT ready: Google + both IT groups; not applicable counts as done. Shown
 *   only when Google's side is known too (configured and checked), for the
 *   same reason.
 * @returns {{ showCounter: boolean, done: number, total: number, allDone: boolean }}
 */
export function combinedProgress({ googleAppKind = [], googleKnown = false, itStatus, itData }) {
  const gTotal = googleAppKind.length;
  const gDone = googleAppKind.filter((a) => a.closed_at).length;
  const status = itEffectiveStatus(itStatus, itData);
  if (status === 'off') return { showCounter: true, done: gDone, total: gTotal, allDone: gTotal > 0 && gDone === gTotal };
  if (status !== 'ready') return { showCounter: false, done: 0, total: 0, allDone: false };
  const items = allItems(itData);
  const done = gDone + items.filter((i) => i.resolution).length;
  const total = gTotal + items.length;
  return { showCounter: googleKnown, done, total, allDone: googleKnown && total > 0 && done === total };
}

// Open items stay in their group; resolved ones (closed or not applicable)
// join the shared Closed area.
export function splitItItems(itData) {
  const open = (list) => (list || []).filter((i) => !i.resolution);
  return {
    closeManually: open(itData && itData.close_manually),
    removeSeat: open(itData && itData.remove_seat),
    resolved: allItems(itData).filter((i) => i.resolution),
  };
}

// One app can have both a close item and a seat item (Microsoft 365, Claude,
// Zoho...). The seat one is named "{app} seat" in checkbox and button labels
// and toasts so screen readers and toasts can tell the two apart.
export const itemLabel = (item) => (item.item_type === 'remove_seat' ? `${item.name} seat` : item.name);
export const itRowId = (item) => `ca-it-${item.app_id}-${item.item_type}`;

export function filterItItems(items, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return items;
  return items.filter((i) => String(i.name || '').toLowerCase().includes(q));
}

// The server already builds the href from an allowlisted shape; the client
// still refuses anything that is not https before putting it in an <a>.
export const safeConsoleHref = (href) => (typeof href === 'string' && /^https:\/\/[^\s]+$/i.test(href) ? href : null);

const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function leaverBanner(leaver, today = new Date()) {
  if (!leaver) return null;
  if (leaver.status === 'left') {
    return leaver.odoo_last_seen_at
      ? `Left Odoo · last seen ${formatDate(leaver.odoo_last_seen_at)}`
      : 'Left Odoo · left before this list started';
  }
  if (leaver.status === 'leaving_soon') {
    const day = parseYmd(leaver.departure_date);
    if (!day) return 'Leaving soon';
    return leaver.departure_date < ymd(today)
      ? `Leaving soon · last day was ${formatDate(day)}`
      : `Leaving soon · last day ${formatDate(day)}`;
  }
  if (leaver.status === 'deactivated') return 'Account deactivated';
  return null;
}
